import React from 'react';
import {
  largeCurrencyFormat,
  oneDecimalPercentFormatValidateFloatTrue,
  twoDecimalPercentFormat,
  yearsDecimalFormat,
} from 'common/formats/formats';
import { SelectValueViewer } from 'components';
import { GridDate, GridDateEditor, GridDateValueViewer } from 'components/FeaturedSpreadsheet/components';
import { SpreadsheetConfig } from 'components/ScalarSpreadsheet/utilities/SpreadsheetConfig';
import { CapTableListSelection } from 'pages/ValuationsAllocation/components/CapTableListSelection';
import { gridShortDate } from 'utilities';
import {
  CAP_TABLE_SELECTION_ALIAS,
  CAP_TABLE_SELECTION_TITLE,
  EQUITY_VALUE_ALIAS,
  EQUITY_VALUE_TITLE,
  getOPMCalculatorInputsWorkbookKey,
  HEADER_ROW_CLASS_NAME,
  MATURITY_ALIAS,
  MATURITY_TITLE,
  OPM_CALCULATOR_INPUTS_COLUMN_HEADER,
  OPM_CALCULATOR_MATURITY_DECIMAL_PLACES,
  OPM_CALCULATOR_MATURITY_MAX_VALUE,
  OPM_CALCULATOR_SPREADSHEET_TITLE,
  OPM_DATE_ALIAS,
  OPM_DATE_TITLE,
  RISK_FREE_RATE_ALIAS,
  RISK_FREE_RATE_TITLE,
  VOLATILITY_ALIAS,
  VOLATILITY_TITLE,
} from '../constants';
import { CapTableOption, OPMCalculatorInputs } from '../types';

interface CreateInputsConfigParams {
  panelId: string;
  inputs: OPMCalculatorInputs;
  /** ISO date shown when `inputs.opmDate` is unset: the one the ladder is actually priced at. */
  fallbackOPMDate?: string;
  capTableList: CapTableOption[];
  isDisabled?: boolean;
  /**
   * Inputs a "Show OPM Calculations" link copied from its source (Equity Allocation, Backsolve,
   * Future Exit). They are read-only: the number belongs to the source surface.
   */
  lockedInputs?: Array<keyof OPMCalculatorInputs>;
}

type InputsTableData = Omit<CreateInputsConfigParams, 'panelId'>;

const isInputReadOnly = (
  input: keyof OPMCalculatorInputs,
  { isDisabled, lockedInputs }: Pick<InputsTableData, 'isDisabled' | 'lockedInputs'>
) => Boolean(isDisabled || lockedInputs?.includes(input));

/**
 * Inputs sheet -- the only editable table on the page.
 *
 * Row titles live in the spreadsheet's own titles column (`showTitlesColumn`, the framework
 * default), fed from `rowConfig[].value`, so column A here is the single *value* column. An
 * earlier version put the titles in column A as data with `showTitlesColumn: false`, which
 * made the sheet two 175px content columns wide and pushed the values out of the panel.
 *
 * Row 1 is the header row: the titles column shows the table name and column A shows "Inputs".
 *
 * The parser closes over the factory arguments rather than reading `tableData`, so it cannot
 * blow up if the workbook calls it without one. The three result sheets do the same.
 */
const buildParser = ({ inputs, fallbackOPMDate, capTableList, isDisabled, lockedInputs }: InputsTableData) => () => ({
  A1: { key: 'A1', value: OPM_CALCULATOR_INPUTS_COLUMN_HEADER, readOnly: true, gridType: 'string' },

  A2: {
    key: 'A2',
    alias: CAP_TABLE_SELECTION_ALIAS,
    value: inputs.capTableId ?? null,
    dbType: 'number',
    dropdown: true,
    isRequired: true,
    readOnly: isInputReadOnly('capTableId', { isDisabled, lockedInputs }),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dataEditor: (props: any) => <CapTableListSelection {...props} />,
    // `useAliasList={false}`: the shared `cap_table` alias handler labels an empty cell with the
    // primary cap table's name, which is right for allocation scenarios (the backend defaults
    // them to the primary) but here read as a selection that was never made -- the results
    // stayed blank with a cap table apparently chosen. Without it the cell shows SELECT OPTION.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    valueViewer: (props: any) => (
      <SelectValueViewer
        options={capTableList}
        objectOptions={{ displayKey: 'name', valueKey: 'id' }}
        useAliasList={false}
        {...props}
      />
    ),
  },
  A3: {
    key: 'A3',
    alias: EQUITY_VALUE_ALIAS,
    value: inputs.equityValue ?? 0,
    dbType: 'string',
    format: largeCurrencyFormat,
    gridType: 'number',
    greaterThan: 0,
    isRequired: true,
    readOnly: isInputReadOnly('equityValue', { isDisabled, lockedInputs }),
  },
  A4: {
    key: 'A4',
    alias: RISK_FREE_RATE_ALIAS,
    value: inputs.riskFreeRate ?? 0,
    format: twoDecimalPercentFormat({ validateFloat: true }),
    gridType: 'percentage',
    // Derived from maturity via FRED, never typed: the rate the analyst sees and the rate
    // the math uses must come from the same (maturity, date) pair.
    readOnly: true,
  },
  A5: {
    key: 'A5',
    alias: MATURITY_ALIAS,
    value: inputs.maturity ?? 0,
    dbDecimalPlaces: OPM_CALCULATOR_MATURITY_DECIMAL_PLACES,
    dbType: 'string',
    // Editing digits match the display digits: the column stores two decimals, so offering a
    // wider editing precision only produced values the results endpoint rejects.
    format: {
      ...yearsDecimalFormat,
      maximumFractionDigits: OPM_CALCULATOR_MATURITY_DECIMAL_PLACES,
      maximumFractionDigitsWhenEditing: OPM_CALCULATOR_MATURITY_DECIMAL_PLACES,
    },
    gridType: 'number',
    greaterThan: 0,
    maxValue: OPM_CALCULATOR_MATURITY_MAX_VALUE,
    isRequired: true,
    readOnly: isInputReadOnly('maturity', { isDisabled, lockedInputs }),
  },
  A6: {
    key: 'A6',
    alias: VOLATILITY_ALIAS,
    value: inputs.volatility ?? 0,
    dbDecimalPlaces: 4,
    dbType: 'string',
    format: oneDecimalPercentFormatValidateFloatTrue,
    gridType: 'percentage',
    greaterThan: 0,
    isRequired: true,
    readOnly: isInputReadOnly('volatility', { isDisabled, lockedInputs }),
  },
  // Same date cell as the Backsolve OPM inputs' "Backsolve Date". Shows the market-date fallback
  // when the row has no date of its own, so the analyst always sees the date being priced at.
  A7: {
    key: 'A7',
    alias: OPM_DATE_ALIAS,
    value: inputs.opmDate || fallbackOPMDate ? gridShortDate(inputs.opmDate || fallbackOPMDate) : null,
    gridType: 'gridDate',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dataEditor: (props: any) => <GridDateEditor {...props} />,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    valueViewer: (props: any) => <GridDateValueViewer {...props} />,
    gridDateComponent: GridDate,
    format: null,
    dbType: 'dbDate',
    readOnly: isInputReadOnly('opmDate', { isDisabled, lockedInputs }),
  },
});

const createInputsConfig = ({
  panelId,
  inputs,
  fallbackOPMDate,
  capTableList,
  isDisabled,
  lockedInputs,
}: CreateInputsConfigParams) => {
  const rowTemplate = { ignoreRowCopy: true };

  return new SpreadsheetConfig({
    name: getOPMCalculatorInputsWorkbookKey(panelId),
    parser: buildParser({ inputs, fallbackOPMDate, capTableList, isDisabled, lockedInputs }),
    rowConfig: [
      {
        ...rowTemplate,
        value: OPM_CALCULATOR_SPREADSHEET_TITLE,
        className: HEADER_ROW_CLASS_NAME,
        gridType: 'string',
        largeHeader: true,
        readOnly: true,
      },
      { ...rowTemplate, value: CAP_TABLE_SELECTION_TITLE },
      { ...rowTemplate, value: EQUITY_VALUE_TITLE },
      { ...rowTemplate, value: RISK_FREE_RATE_TITLE },
      { ...rowTemplate, value: MATURITY_TITLE },
      { ...rowTemplate, value: VOLATILITY_TITLE },
      { ...rowTemplate, value: OPM_DATE_TITLE },
    ],
    // A single value column; the titles column is rendered by the spreadsheet itself.
    columns: [{}],
    tableData: { inputs, fallbackOPMDate, capTableList, isDisabled, lockedInputs },
    tableTerms: {
      tableName: OPM_CALCULATOR_SPREADSHEET_TITLE,
      tableSlug: 'opm-calculator-inputs',
      columnName: OPM_CALCULATOR_INPUTS_COLUMN_HEADER,
    },
    showToolbar: false,
    showTotalColumn: false,
    currencyFormatter: false,
    unitsFormatter: false,
    allowCopyColumn: false,
    allowReorderColumns: false,
    allowConfirmAndDeleteColumn: false,
  } as unknown as ConstructorParameters<typeof SpreadsheetConfig>[0]);
};

export default createInputsConfig;
